import { useState } from 'react'
import {
  AlertTriangle,
  ArrowLeft,
  ExternalLink,
  FileDown,
  Library,
  Lock,
  ScrollText,
  ShieldAlert,
} from 'lucide-react'
import type { KbQuelle, LizenzklasseValue } from '@mindkeep/shared'
import {
  useKbQuelleDetail,
  useKbQuellen,
  useNachweisExport,
} from '@/features/kb/use-kb-quellen'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { PageHeader } from '@/components/page-header'
import { EmptyState } from '@/components/empty-state'
import { ErrorState } from '@/components/error-state'
import { SkeletonList } from '@/components/skeleton-list'

/**
 * Quellenregister (RNA M6).
 *
 * Die Seite zeigt, was die Datenbank erzwingt — sie erzwingt es nicht selbst.
 * Der Schichtzwang, das Ingest-Gate und die Regel «gesperrt heisst nur
 * Fundstellenkarte» liegen in Migration 031 als Constraints und Trigger. Hier
 * werden sie lesbar gemacht, damit niemand erst am Fehlschlag merkt, wo die
 * Lizenzgrenze verlaeuft.
 *
 * Die Lizenzklasse ist deshalb bewusst nicht editierbar: sie steht im
 * Frontmatter der Quelldatei, und die Datei bleibt die Wahrheit.
 */

const LIZENZ_LABEL: Record<LizenzklasseValue, string> = {
  frei: 'Frei',
  lizenziert_derivat: 'Lizenziertes Derivat',
  gesperrt: 'Gesperrt',
}

const LIZENZ_ERKLAERUNG: Record<LizenzklasseValue, string> = {
  frei: 'Frei publizierte Quelle. Volle Verarbeitung, darf in die geteilte Schicht.',
  lizenziert_derivat:
    'Kostenpflichtige Quelle mit Lizenz des Mandanten. Das Derivat bleibt in der '
    + 'Mandantenschicht des Lizenzhalters — die Datenbank laesst nichts anderes zu.',
  gesperrt:
    'Keine Lizenz oder Verarbeitungsverbot. Es existiert nur die Fundstellenkarte: '
    + 'kein Inhalt, kein Wert, keine Anforderung.',
}

function LizenzBadge({ klasse }: { klasse: LizenzklasseValue | null }) {
  if (klasse === null) {
    return (
      <Badge variant="destructive" className="gap-1">
        <ShieldAlert className="h-3 w-3" />
        Keine Lizenzklasse
      </Badge>
    )
  }
  if (klasse === 'frei') {
    return <Badge className="bg-success text-success-foreground">Frei</Badge>
  }
  if (klasse === 'lizenziert_derivat') {
    return (
      <Badge className="gap-1 bg-warning text-warning-foreground">
        <Lock className="h-3 w-3" />
        Lizenziertes Derivat
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="gap-1">
      <Lock className="h-3 w-3" />
      Gesperrt
    </Badge>
  )
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function QuellenregisterPage() {
  const [selected, setSelected] = useState<string | null>(null)
  const list = useKbQuellen()

  if (selected) {
    return <QuelleDetail path={selected} onBack={() => setSelected(null)} />
  }

  const quellen = list.data?.quellen ?? []
  const ohneKlasse = quellen.filter((quelle) => quelle.lizenzklasse === null)
  const erfasst = quellen.filter((quelle) => quelle.lizenzklasse !== null)

  return (
    // R1: gemeinsamer Flow-Arbeitsrahmen — max 1180px, mobil 16px Rand.
    <div className="mx-auto w-full max-w-[1180px] px-4 py-6 min-[761px]:px-12 min-[761px]:py-8">
      <PageHeader
        title="Quellenregister"
        description="Jede kuratierte Quelle mit Herkunft und Lizenzklasse. Ohne erklärte Lizenzklasse wird nichts eingelesen — die Prüfung erfolgt beim Einlesen, nicht erst bei der Nutzung."
      />

      {list.data?.ist_wissensbibliothek && (
        <p className="mb-6 flex items-start gap-2 rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
          <Library className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Dieser Workspace ist eine Wissensbibliothek und wird von anderen Mandanten gelesen.
            Lizenzierte Derivate koennen hier nicht abgelegt werden — der Schreibvorgang schlaegt
            fehl.
          </span>
        </p>
      )}

      {list.isLoading ? (
        <SkeletonList rows={5} />
      ) : list.isError ? (
        <ErrorState
          title="Quellenregister konnte nicht geladen werden"
          description={
            list.error instanceof Error ? list.error.message : 'Bitte versuche es erneut.'
          }
          onRetry={() => void list.refetch()}
          isRetrying={list.isFetching}
        />
      ) : quellen.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title="Noch keine Quellen erfasst"
          description="Sobald ein kuratiertes Wissensdokument mit Lizenzklasse abgelegt wird, erscheint es hier."
        />
      ) : (
        <div className="space-y-8">
          {ohneKlasse.length > 0 && (
            <section className="space-y-3">
              <h2 className="flex items-center gap-2 text-[16px] font-[650] tracking-tight text-foreground">
                <AlertTriangle className="h-4 w-4 shrink-0 text-brand-danger" />
                Nicht eingelesen — Lizenzklasse fehlt ({ohneKlasse.length})
              </h2>
              <p className="text-sm text-muted-foreground">
                Diese Dokumente liegen in der Ablage, sind aber nicht im Index. Es gibt keinen
                Standardwert: die Klasse muss im Frontmatter erklaert werden.
              </p>
              {ohneKlasse.map((quelle) => (
                <QuellenZeile
                  key={quelle.path}
                  quelle={quelle}
                  onOpen={() => setSelected(quelle.path)}
                />
              ))}
            </section>
          )}

          <section className="space-y-3">
            <h2 className="text-[16px] font-[650] tracking-tight text-foreground">
              Erfasste Quellen ({erfasst.length})
            </h2>
            {erfasst.length === 0 ? (
              <p className="text-sm text-muted-foreground">Keine Quelle mit erklaerter Klasse.</p>
            ) : (
              erfasst.map((quelle) => (
                <QuellenZeile
                  key={quelle.path}
                  quelle={quelle}
                  onOpen={() => setSelected(quelle.path)}
                />
              ))
            )}
          </section>
        </div>
      )}
    </div>
  )
}

function QuellenZeile({ quelle, onOpen }: { quelle: KbQuelle; onOpen: () => void }) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-start gap-4 p-4">
        <div className="min-w-0 flex-1 basis-64">
          <div className="flex flex-wrap items-center gap-2">
            <p className="min-w-0 break-words text-sm font-medium text-foreground">{quelle.titel}</p>
            <LizenzBadge klasse={quelle.lizenzklasse} />
            {quelle.tier !== null && <Badge variant="outline">Tier {quelle.tier}</Badge>}
            {quelle.nur_fundstellenkarte && (
              <Badge variant="secondary" className="break-words">Nur Fundstellenkarte</Badge>
            )}
          </div>
          <p className="break-all font-mono text-xs text-muted-foreground">{quelle.path}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {quelle.herausgeber ?? 'Herausgeber nicht belegt'}
            {quelle.quellen_typ ? ` · ${quelle.quellen_typ}` : ''}
            {quelle.fassung ? ` · Fassung ${quelle.fassung}` : ''}
            {` · ${quelle.chunks_count} Ausschnitte`}
          </p>
          {quelle.error_reason && (
            <p className="mt-2 border-l-2 border-brand-danger pl-2 text-xs text-muted-foreground">
              {quelle.error_reason}
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={onOpen}>
          Nachweis
        </Button>
      </CardContent>
    </Card>
  )
}

function QuelleDetail({ path, onBack }: { path: string; onBack: () => void }) {
  const detail = useKbQuelleDetail(path)
  const exportPdf = useNachweisExport()
  const daten = detail.data

  return (
    // R1: Detail-Lesebreite 720px, mobil 16px Rand.
    <div className="mx-auto w-full max-w-[720px] px-4 py-6 min-[761px]:px-6 min-[761px]:py-8">
      <Button variant="ghost" size="sm" className="mb-4" onClick={onBack}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Zurueck zum Quellenregister
      </Button>

      {detail.isLoading ? (
        <SkeletonList rows={4} />
      ) : detail.isError || !daten ? (
        <ErrorState
          title="Quelle konnte nicht geladen werden"
          description={
            detail.error instanceof Error ? detail.error.message : 'Bitte versuche es erneut.'
          }
          onRetry={() => void detail.refetch()}
          isRetrying={detail.isFetching}
        />
      ) : (
        <div className="space-y-6">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-[26px] font-[650] tracking-tight break-words text-foreground">
                {daten.quelle.titel}
              </h1>
              <LizenzBadge klasse={daten.quelle.lizenzklasse} />
            </div>
            <p className="mt-1 break-all font-mono text-xs text-muted-foreground">{daten.quelle.path}</p>
            {daten.quelle.lizenzklasse && (
              <p className="mt-2 text-sm text-muted-foreground">
                {LIZENZ_ERKLAERUNG[daten.quelle.lizenzklasse]}
              </p>
            )}
            {daten.quelle.lizenzklasse === null && daten.quelle.error_reason && (
              <p className="mt-2 rounded-md border border-brand-danger/40 bg-muted px-3 py-2 text-sm text-foreground">
                {daten.quelle.error_reason}
              </p>
            )}
          </div>

          <Card>
            <CardContent className="space-y-3 p-6">
              <h2 className="text-[16px] font-[650] tracking-tight text-foreground">Herkunft</h2>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                <Feld label="Herausgeber" wert={daten.quelle.herausgeber} />
                <Feld label="Typ der Quelle" wert={daten.quelle.quellen_typ} />
                <Feld label="Fassung" wert={daten.quelle.fassung} />
                <Feld
                  label="Tier"
                  wert={daten.quelle.tier === null ? null : String(daten.quelle.tier)}
                />
                <Feld label="Abrufdatum" wert={formatDate(daten.quelle.abrufdatum)} />
                <Feld label="Gueltig von" wert={daten.quelle.gueltig_von} />
                <Feld label="Gueltig bis" wert={daten.quelle.gueltig_bis ?? 'aktuell'} />
                <Feld label="Freigabe" wert={daten.quelle.freigabe_status} />
                <Feld label="Ausschnitte" wert={String(daten.quelle.chunks_count)} />
              </dl>
              {daten.quelle.bezugs_url && (
                <a
                  href={daten.quelle.bezugs_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-sm text-brand-accent hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  Bezugsweg
                </a>
              )}
            </CardContent>
          </Card>

          {daten.fundstellenkarte.length > 0 && (
            <Card>
              <CardContent className="space-y-3 p-6">
                <h2 className="flex items-center gap-2 text-[16px] font-[650] tracking-tight text-foreground">
                  <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
                  Fundstellenkarte
                </h2>
                <p className="text-xs text-muted-foreground">
                  Die Quelle ist gesperrt. Es existiert nur diese Karte — kein Inhalt, kein Wert,
                  keine Anforderung.
                </p>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                  {daten.fundstellenkarte.map((eintrag) => (
                    <Feld key={eintrag.feld} label={eintrag.feld} wert={eintrag.wert} />
                  ))}
                </dl>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="space-y-4 p-6">
              <div>
                <h2 className="text-[16px] font-[650] tracking-tight text-foreground">Nachweis</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Diese Angaben machen eine spaetere rechtliche Pruefung billig: sie belegen, dass
                  die Auswahl aufgabenbezogen ist und der Wortlaut nicht uebernommen wurde.
                </p>
              </div>

              <div>
                <p className="text-xs font-medium text-muted-foreground">Aufgabenliste</p>
                {daten.nachweis.aufgabenliste.length === 0 ? (
                  <p className="mt-1 text-sm text-muted-foreground">
                    Nicht belegt. Ohne Aufgabenliste ist die Auswahl nicht rueckbindbar.
                  </p>
                ) : (
                  <ul className="mt-1 list-inside list-disc space-y-0.5 text-sm text-foreground">
                    {daten.nachweis.aufgabenliste.map((frage) => (
                      <li key={frage}>{frage}</li>
                    ))}
                  </ul>
                )}
              </div>

              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                <Feld
                  label="Quelle, Seiten"
                  wert={
                    daten.nachweis.quelle_umfang_seiten === null
                      ? null
                      : String(daten.nachweis.quelle_umfang_seiten)
                  }
                />
                <Feld
                  label="Derivat, Woerter"
                  wert={
                    daten.nachweis.derivat_umfang_woerter === null
                      ? null
                      : String(daten.nachweis.derivat_umfang_woerter)
                  }
                />
                <Feld label="Verdichtungsgrad" wert={daten.nachweis.verdichtungsgrad} />
                <Feld
                  label="Laengste Wortfolge"
                  wert={
                    daten.nachweis.laengste_wortfolge === null
                      ? null
                      : `${daten.nachweis.laengste_wortfolge} Woerter`
                  }
                />
              </dl>

              {daten.nachweis.wortlautgrenze_verletzt && (
                <p className="flex items-start gap-2 rounded-md border border-brand-danger/40 bg-muted px-3 py-2 text-sm text-foreground">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-brand-danger" />
                  <span>
                    Die Grenze von sieben deckungsgleichen Woertern ist ueberschritten. Die
                    betroffenen Stellen muessen umformuliert werden, bevor das Derivat weitergegeben
                    wird.
                  </span>
                </p>
              )}

              <div className="border-t pt-4">
                <Button
                  variant="outline"
                  onClick={() => exportPdf.mutate(daten.quelle.path)}
                  disabled={exportPdf.isPending}
                >
                  <FileDown className="mr-2 h-4 w-4" />
                  Derivat und Nachweis als PDF
                </Button>
                <p className="mt-2 text-xs text-muted-foreground">
                  Vorlage fuer eine rechtliche Pruefung. Keine Rechtsberatung.
                </p>
              </div>
            </CardContent>
          </Card>

          {daten.quelle.lizenzklasse && (
            <p className="text-xs text-muted-foreground">
              Lizenzklasse {LIZENZ_LABEL[daten.quelle.lizenzklasse]} — sie steht im Frontmatter der
              Datei und wird hier nicht geaendert. Wer die Klasse aendern will, aendert das
              Dokument; das Register zieht beim naechsten Einlesen nach.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function Feld({ label, wert }: { label: string; wert: string | null }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{wert ?? '—'}</dd>
    </div>
  )
}
